import React from 'react';
import { useIntl } from 'react-intl';

import { SMSProviders } from '../../../api';
import { SMS_LINKS } from '../../../constants';
import { FieldInput } from '../../ui';
import {
  DescriptionLink,
  FieldWrapper,
  ProviderSubtitle,
  ProviderTitle,
  SmsProviderFieldsCard,
  SmsProviderHeader
} from '../style';
import { SMSProviderStatus } from './SMSProviderStatus';
import { ProviderFieldProps } from './types';
import { maxFieldLength, maxPhoneNumberLength } from './validator';

export const AmazonSESFields = ({ errors, providerData }: ProviderFieldProps): JSX.Element => {
  const intl = useIntl();
  const { AMAZON_SENDER, AMAZON_SNS, AMAZON_REGION } = SMS_LINKS;

  const getError = (field: string): string => {
    if (errors[SMSProviders.amazon][field]) {
      return intl.formatMessage(
        { id: errors[SMSProviders.amazon][field] as string },
        {
          number: field === 'phone_number' ? maxPhoneNumberLength : maxFieldLength
        }
      );
    }

    return '';
  };

  return (
    <SmsProviderFieldsCard data-test-id="sms-provider-fields-card-amazon">
      <SmsProviderHeader>
        <ProviderTitle>
          {intl.formatMessage({ id: 'custom-providers.settings.sms.provider.amazon.label' })}
        </ProviderTitle>
        <SMSProviderStatus
          providerData={providerData}
          dataTestId="sms-provider-amazon-status-button"
        />
      </SmsProviderHeader>
      <ProviderSubtitle>
        {intl.formatMessage(
          { id: 'custom-providers.settings.sms.provider.amazon.description' },
          {
            a: (chunk: string) => (
              <DescriptionLink href={AMAZON_SNS} target="_blank">
                {chunk}
              </DescriptionLink>
            )
          }
        )}
      </ProviderSubtitle>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.amazon.phone.label'
          })}
          errorMessage={getError('phone_number')}
          name={`fields.${SMSProviders.amazon}.phone_number`}
        />
      </FieldWrapper>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.amazon.key-id.label'
          })}
          errorMessage={getError('app_id')}
          name={`fields.${SMSProviders.amazon}.app_id`}
        />
      </FieldWrapper>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.amazon.access-key.label'
          })}
          errorMessage={getError('app_secret')}
          name={`fields.${SMSProviders.amazon}.app_secret`}
        />
      </FieldWrapper>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.amazon.region.label'
          })}
          subLabel={intl.formatMessage(
            {
              id: 'custom-providers.settings.sms.provider.amazon.region.sublabel'
            },
            {
              a: (chunk: string) => (
                <DescriptionLink href={AMAZON_REGION} target="_blank">
                  {chunk}
                </DescriptionLink>
              )
            }
          )}
          errorMessage={getError('aws_region')}
          name={`fields.${SMSProviders.amazon}.aws_region`}
        />
      </FieldWrapper>
      <FieldWrapper>
        <FieldInput
          label={intl.formatMessage({
            id: 'custom-providers.settings.sms.provider.sender-id.label'
          })}
          subLabel={intl.formatMessage(
            {
              id: 'custom-providers.settings.sms.provider.sender-id.sublabel'
            },
            {
              a: (chunk: string) => (
                <DescriptionLink href={AMAZON_SENDER} target="_blank">
                  {chunk}
                </DescriptionLink>
              )
            }
          )}
          errorMessage={getError('sender_id')}
          name={`fields.${SMSProviders.amazon}.sender_id`}
        />
      </FieldWrapper>
    </SmsProviderFieldsCard>
  );
};
